import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
interface IndustriesHubProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function IndustriesHub({
  setPage
}: IndustriesHubProps) {
  const industries = [{
    title: 'Automotive & Fleet',
    desc: 'From classic car restorations to commercial fleet maintenance, we safely strip paint, rust, and body filler down to bare metal without warping panels.',
    image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=800&auto=format&fit=crop'
  }, {
    title: 'Construction & Heavy Equipment',
    desc: 'Extend the life of your heavy machinery. We remove years of rust, industrial coatings, and concrete splatter from excavators, loaders, and cranes.',
    image: 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?q=80&w=800&auto=format&fit=crop'
  }, {
    title: 'Commercial Properties',
    desc: 'Restore building facades, remove graffiti, and clean parking structures. Our dustless process is ideal for urban environments and active commercial sites.',
    image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=800&auto=format&fit=crop'
  }, {
    title: 'Agriculture & Farming',
    desc: 'Keep your farm equipment running and looking like new. We blast tractors, implements, and silos to prepare them for protective recoating.',
    image: 'https://images.unsplash.com/photo-1592982537447-6f2a6a0a3023?q=80&w=800&auto=format&fit=crop'
  }, {
    title: 'Marine & Boating',
    desc: 'Safe removal of bottom paint, anti-fouling coatings, and marine growth from fiberglass, aluminum, and steel hulls without damaging the gelcoat.',
    image: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?q=80&w=800&auto=format&fit=crop'
  }, {
    title: 'Industrial & Manufacturing',
    desc: 'Plant maintenance, tank cleaning, and structural steel preparation. We handle large-scale industrial stripping with minimal downtime.',
    image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=800&auto=format&fit=crop'
  }];
  return <div className="w-full bg-white" data-id="element-405">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-406">
        <div className="max-w-7xl mx-auto text-center" data-id="element-407">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-408">
            Industries We <span className="text-brand-red" data-id="element-409">Serve</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed" data-id="element-410">
            Merem Blasting provides specialized surface preparation solutions
            tailored to the unique demands and compliance requirements of
            various industries across Michigan.
          </motion.p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white" data-id="element-411">
        <div className="max-w-7xl mx-auto px-4" data-id="element-412">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-413">
            {industries.map((ind, idx) => <motion.div key={idx} variants={fadeInUp} className="group flex flex-col" data-id="element-414">
                <div className="relative h-64 md:h-80 overflow-hidden rounded-sm mb-6" data-id="element-415">
                  <img src={ind.image} alt={ind.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-416" />
                  <div className="absolute inset-0 bg-charcoal/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-417"></div>
                </div>
                <h3 className="text-2xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-418">
                  {ind.title}
                </h3>
                <p className="text-steel-600 leading-relaxed mb-6 flex-grow" data-id="element-419">
                  {ind.desc}
                </p>
                <Button variant="ghost" onClick={() => setPage('contact')} className="self-start px-0 hover:bg-transparent hover:text-brand-red" data-id="element-420">
                  Discuss Your Project →
                </Button>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand-red text-white text-center px-4" data-id="element-421">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-422">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-423">
            Need Specialized Blasting?
          </h2>
          <p className="text-xl font-body mb-10 text-white/90" data-id="element-424">
            We have the equipment and expertise to handle complex commercial and
            industrial projects.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-425">
            <Button variant="secondary" size="lg" onClick={() => setPage('contact')} showPhoneIcon className="w-full sm:w-auto border-none" data-id="element-426">
              Call (517) 777-8088
            </Button>
            <Button variant="outline-red" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto" data-id="element-427">
              Request a Quote
            </Button>
          </div>
        </motion.div>
      </section>
    </div>;
}